import React, { forwardRef, Fragment, useContext, useEffect, useMemo, useState, } from 'react';
import { BlockContext } from './BlockViewer.js';
import { useEditor } from './blockEditorHooks.js';
const ChildrenBlock = forwardRef(function ChildrenBlockRenderer({ gutenbergEditorConfig, childrenProps, childrenBlockComponent, ...props }, ref) {
    const blockContext = useContext(BlockContext);
    const { isEditing } = useEditor();
    const ChildrenBlockComponent = childrenBlockComponent ?? blockContext?.childrenBlockRenderer;
    const childrenContext = useMemo(() => ({
        parentBlock: blockContext,
        childrenProps: childrenProps,
    }), [blockContext, props]);
    const [useInnerBlocksProps, setUseInnerBlocksProps] = useState();
    useEffect(() => {
        if (isEditing) {
            import('./wordpress/editorApi.js').then((m) => setUseInnerBlocksProps(() => m.useInnerBlocksProps));
        }
    }, [isEditing]);
    let render = React.createElement(SmartfireChildrenBlocks, {
        ...props,
        childrenBlockComponent: ChildrenBlockComponent,
        ref,
    });
    if (isEditing) {
        if (useInnerBlocksProps) {
            render = React.createElement(SmartfireInnerBlocks, {
                ...props,
                useInnerBlocksProps,
                gutenbergEditorConfig,
                childrenBlockComponent: ChildrenBlockComponent,
                ref: ref,
            });
        }
        else {
            render = React.createElement(Fragment);
        }
    }
    return React.createElement(ChildrenBlockContext.Provider, {
        key: 'children-block-context-' + blockContext?.id,
        value: childrenContext,
    }, render);
});
const SmartfireInnerBlocks = forwardRef(function SmartfireInnerBlocks({ useInnerBlocksProps, childrenBlockComponent, gutenbergEditorConfig, ...props }, ref) {
    const blocksProps = useInnerBlocksProps({ ...props, ref }, gutenbergEditorConfig);
    const type = childrenBlockComponent ?? 'div';
    return React.createElement(type, blocksProps);
});
const SmartfireChildrenBlocks = forwardRef(function SmartfireInnerBlocks({ childrenBlockComponent, gutenbergEditorConfig, ...props }, ref) {
    const blockContext = useContext(BlockContext);
    const type = childrenBlockComponent ?? 'div';
    return React.createElement(type, { ...props, ref }, blockContext?.children);
});
export default ChildrenBlock;
export const ChildrenBlockContext = React.createContext(undefined);
export function useChildrenBlocks() {
    const { isEditing } = useEditor();
    const blockContext = useContext(BlockContext);
    const children = blockContext?.children;
    const Child = useMemo(() => {
        let child = forwardRef(function Child({ index, child, elementType, ...props }, ref) {
            const childrenContext = useMemo(() => ({
                parentBlock: blockContext,
                unique: true,
                childrenProps: props,
                forwardedChildrenRef: ref,
            }), [blockContext, props]);
            const ChildValue = child ?? children?.[index];
            if (isEditing) {
                throw new Error('Single child is not available from edition context');
            }
            if (!ChildValue) {
                throw new Error('No child available on index ' + index);
            }
            if (elementType) {
                throw new Error('Wrapping inside a specific element is not supported');
            }
            return React.createElement(ChildrenBlockContext.Provider, {
                value: childrenContext,
            }, ChildValue);
        });
        Object.defineProperty(child, 'name', { value: 'BlockChild-' + (blockContext?.Block.name ?? 'Raw') });
        return child;
    }, [children, isEditing]);
    const Children = ChildrenBlock;
    return {
        Child, Children, children: isEditing ? undefined : children,
    };
}

//# sourceMappingURL=ChildrenBlock.js.map
